import { GetMaybeType } from "@mx/typescript-toolbox/typings";
import * as React from "react";
import { ensureError } from "../../core/error";
import { useDidUpdate } from "../../core/hooks/useDidUpdate";
import logger from "../../core/logger";
import { downloadInNewWindow } from "../../core/utils";
import { IDownloadProcedureMutation } from "../../graphql/gpl-graphQLTypes.gen";
import { trackGPLEvent } from "../../providers/GoogleTagManager/GoogleTagManagerProvider";
import { NoArgCallback } from "../../typings/utils";
import { DownloadIcon } from "../Icons/Icons";
import Input from "../Input/Input";
import Modal, { ModalButton, ModalCopy, ModalHeading, ModalInput } from "../Modal/Modal";

export type DownloadPDFCallback = (
  email: string,
) => Promise<GetMaybeType<IDownloadProcedureMutation["downloadProcedure"]>>;
interface IProps {
  procedureUid: string;
  show: boolean;
  onHide: NoArgCallback;
  onDownload: DownloadPDFCallback;
}

export default function ModalDownloadPdf({ procedureUid, show, onHide, onDownload }: IProps) {
  const [emailAddress, setEmailAddress] = React.useState("");
  const [errorText, setErrorText] = React.useState("");

  const handleChangeEmail = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    setEmailAddress(value);
    setErrorText("");
  }, []);

  const handleSubmit = React.useCallback(
    async (ev: React.FormEvent) => {
      ev.preventDefault();

      if (!emailAddress) {
        return setErrorText("Please provide an email address");
      }
      if (!emailAddress.includes("@")) {
        return setErrorText("Please provide a valid email address");
      }
      setErrorText("");
      try {
        await downloadInNewWindow(async () => {
          const resp = await onDownload(emailAddress);
          if (resp?.download?.url) {
            trackGPLEvent("DownloadProcedure", {
              action: "Download",
              category: "Procedure",
              label: procedureUid,
            });
            return resp.download.url;
          }
          if (resp.errors?.[0]) {
            setErrorText(resp.errors[0].error);
          } else {
            setErrorText("Oops… Could not download the PDF. Please try again");
          }
          return null;
        }, "Loading PDF…");
      } catch (err) {
        logger.error(ensureError(err), { from: "onDownload" });
        setErrorText("Oops… Could not download the PDF. Please try again");
      }
    },
    [emailAddress, procedureUid, onDownload],
  );

  useDidUpdate(() => {
    // reset state when modal visibility changes
    if (show) {
      trackGPLEvent("ShowDownloadProcedureModal", {
        action: "Click",
        category: "Procedure",
        label: procedureUid,
      });
      setEmailAddress("");
      setErrorText("");
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide}>
      <ModalHeading>Download as PDF</ModalHeading>
      <ModalCopy>To download this procedure as a PDF, please tell us your email address.</ModalCopy>
      <form noValidate={true} onSubmit={handleSubmit}>
        <ModalInput>
          <Input
            autoComplete="email"
            autoFocus={true}
            errorText={errorText}
            helpText="We won’t send you any spam, we promise!"
            name="email"
            placeholder="name@example.com"
            type="email"
            value={emailAddress}
            onChange={handleChangeEmail}
          />
        </ModalInput>
        <ModalButton iconLeft={<DownloadIcon width={16} height={16} />} type="submit">
          Download Procedure PDF
        </ModalButton>
      </form>
    </Modal>
  );
}
